import { useNavigate } from "@tanstack/react-router";
import type { Rule } from "@unbox/api";
import { Edit01, Trash01, ZapFast } from "@untitledui/icons";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useMerchant, useWorkspaceId } from "@/providers/MerchantProvider";
import { formatPostedAgo } from "@/utils/date-conversion";
import { RULE_AREA_DETAILS } from "./rule-areas";

type RulesTableProps = {
  rules: Rule[];
  impacted: Map<string, number>;
  isCountingImpact: boolean;
  impactFailed: boolean;
  onEdit: (rule: Rule) => void;
  onDelete: (rule: Rule) => void;
};

const ImpactedCell = ({
  rule,
  count,
  unavailable,
}: {
  rule: Rule;
  count: number | undefined;
  unavailable: boolean;
}) => {
  const navigate = useNavigate();
  const merchantId = useMerchant();
  const workspaceId = useWorkspaceId();

  if (count === undefined) return <span className="text-quaternary">{unavailable ? "—" : "…"}</span>;

  const label = (
    <>
      <span className="font-semibold">{count}</span> {count === 1 ? "creator" : "creators"}
    </>
  );

  if (count === 0) return <span className="text-secondary">{label}</span>;

  const search = { classification: "__all" as const, ruleArea: rule.area, ruleId: rule.ruleId };
  const showMatches = () =>
    workspaceId
      ? navigate({
          to: "/workspace/$workspaceId/merchant/$merchantId/creators" as string,
          params: { workspaceId, merchantId },
          search,
        })
      : navigate({ to: "/merchant/$merchantId/creators", params: { merchantId }, search });

  return (
    <button
      type="button"
      aria-label={`Show the creators ${rule.name} matches`}
      className="cursor-pointer text-brand-secondary underline decoration-dotted decoration-1 underline-offset-4 transition-colors duration-100 hover:decoration-solid"
      onClick={showMatches}
    >
      {label}
    </button>
  );
};

const NoRulesYet = () => (
  <div className="flex w-full flex-col items-center gap-2 bg-secondary px-8 py-16 text-center">
    <span
      aria-hidden="true"
      className="mb-3 inline-flex size-11 items-center justify-center rounded-full bg-primary text-brand-secondary"
    >
      <ZapFast className="size-[22px]" strokeWidth={1.8} />
    </span>
    <p className="text-base font-semibold text-primary">No rules yet</p>
    <p className="max-w-[42ch] text-sm leading-5 text-tertiary">
      Build your own rules across sample approvals, creator cohorts or blocking creators.
    </p>
  </div>
);

export const RulesTable = ({ rules, impacted, isCountingImpact, impactFailed, onEdit, onDelete }: RulesTableProps) => {
  if (rules.length === 0) return <NoRulesYet />;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-full">Rule name</TableHead>
          <TableHead className="w-px whitespace-nowrap">Status</TableHead>
          <TableHead className="w-px whitespace-nowrap">Area</TableHead>
          <TableHead className="w-px whitespace-nowrap">Impacted</TableHead>
          <TableHead className="w-px whitespace-nowrap">Last updated</TableHead>
          <TableHead className="w-px" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rules.map((rule) => {
          const { label, icon: Icon } = RULE_AREA_DETAILS[rule.area];

          return (
            <TableRow key={rule.ruleId}>
              <TableCell className="font-medium text-primary">{rule.name}</TableCell>
              <TableCell className="whitespace-nowrap">
                <BadgeWithDot type="pill-color" size="md" color={rule.isActive ? "success" : "gray"}>
                  {rule.isActive ? "Enabled" : "Disabled"}
                </BadgeWithDot>
              </TableCell>
              <TableCell className="whitespace-nowrap text-secondary">
                <Icon className="mr-2 inline size-4 align-[-3px]" strokeWidth={1.8} aria-hidden="true" />
                {label}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <ImpactedCell
                  rule={rule}
                  count={isCountingImpact || impactFailed ? undefined : (impacted.get(rule.ruleId) ?? 0)}
                  unavailable={impactFailed}
                />
              </TableCell>
              <TableCell className="whitespace-nowrap text-secondary">{formatPostedAgo(rule.updatedAt)}</TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    color="tertiary"
                    size="sm"
                    iconLeading={Trash01}
                    aria-label={`Delete ${rule.name}`}
                    onClick={() => onDelete(rule)}
                  />
                  <Button
                    color="tertiary"
                    size="sm"
                    iconLeading={Edit01}
                    aria-label={`Edit ${rule.name}`}
                    onClick={() => onEdit(rule)}
                  />
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
